import { useActions, useSelector } from "app/utils/hooks";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { RootState } from "typedefs";
import {
  fetchCoachProfile,
  updateCoachProfile,
} from "app/actions/coachNowCoachProfile";
import { fetchAcademySetup } from "app/actions/academySetup";
import {
  isSetupItemOpen,
  showCoachProfileEnabledToast,
} from "app/components/AcademySetup/SetupToast";
import { isAcademyOnboardingCoach } from "app/utils/subscription";
import { useCallback, useEffect, useState } from "react";
import { IUserPhone } from "app/interfaces/UserTypes";
import customPrompt from "app/components/inputs/SelectBox/helpers/customPrompt";
import { CoachProfileModel } from "app/models/CoachProfileModel";
import {
  schedulingFetchGet,
  schedulingFetchPatch,
} from "../../../utils/request/scheduling/fetch";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { fetchPut } from "app/utils/request";

export const useCoachProfileModel = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { self } = useSelector((state: RootState) => state.user, shallowEqual);
  const { data: coachData } = useSelector(
    (state: RootState) => state.coachProfile,
    shallowEqual
  );
  const actions = useActions({
    fetchCoachProfile,
    updateCoachProfile,
    fetchAcademySetup,
  });

  const [loading, setLoading] = useState(false);
  const [toastAlert, setToastAlert] = useState(null);
  const [toastErrorAlert, setToastErrorrAlert] = useState(null);
  const [userState, setUserState] = useState<CoachProfileModel>(
    coachData || {}
  );
  const [uploadedAvatar, setUploadedAvatar] = useState<string | null>(null);
  const [uploadedCover, setUploadedCover] = useState<string | null>(null);
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [error, setError] = useState<{
    bookingServiceError: string | null;
    paymentServiceError: string | null;
  }>({
    bookingServiceError: null,
    paymentServiceError: null,
  });
  const [publicLandingPageEnabled, setPublicLandingPageEnabled] = useState(false);
  const [publicLandingPageUrl, setPublicLandingPageUrl] = useState("");

  useEffect((): (() => void) => {
    let isMounted = true;
    setLoading(true);

    actions
      .fetchCoachProfile(self?.id)
      .then((res: any): void => {
        if (isMounted && res) {
          setUserState(res);
          if (res?.coverUrl) {
            setUploadedCover(res?.coverUrl);
          }
          if (res?.avatarUrl) {
            setUploadedAvatar(res?.avatarUrl);
          }
          if (res?.phone && res?.phoneCountry) {
            const fullPhone = `+${res.phoneCountry}${res.phone}`;
            setPhoneValue(fullPhone);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return (): void => {
      isMounted = false;
      setLoading(false);
    };
  }, [actions]);

  useEffect(() => {
    if (self?.id) {
      schedulingFetchGet(`coach_profiles/${self.id}`)
        .then((data) => {
          setPublicLandingPageEnabled(data["publicLandingPage"]);
          setPublicLandingPageUrl(data["publicLandingPageUrl"]);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [self?.id]);

  const onUpload = (avatar: { url: string }) => {
    setLoading(true);

    try {
      setUploadedAvatar(avatar?.url);
      updateUserData({ type: "avatar_url", value: avatar?.url });
      setLoading(false);
    } catch (error) {
      console.error("Error uploading file:", error);
      setLoading(false);
    }
  };

  const onCoverChange = (avatar: { id: string; url: string }) => {
    setUploadedCover(avatar?.url);
    setUserState({
      ...userState,
      cover_id: avatar?.id,
    });
  };

  const onPhoneChange = (
    value: string,
    data: IUserPhone,
    _event: React.FormEvent<HTMLInputElement>
    // formattedValue: string,
  ) => {
    const phone = value.substring(data?.dialCode?.length);
    const phoneCountry = data?.countryCode?.toUpperCase();
    console.log(phone);
    setPhoneValue(value);
    setUserState({
      ...userState,
      phone: value,
      phoneCountry,
    });
  };

  const updateUserData = useCallback(
    ({ type, value }: { type: string; value: string | boolean }) => {
      setUserState((prev) => ({ ...prev, [type]: value }));
    },
    []
  );

  const isValidUrl = (value: string) =>
    value === "" || /^https?:\/\/[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(value);

  const handleUrlInputUpdate = useCallback(
    ({ type, value }: { type: string; value: string }) => {
      const trimmedValue = value.replace(/\s+/g, "");

      const errorMessage = t(
        "Please provide a valid URL (e.g., https://example.com or www.example.com)."
      );

      if (trimmedValue !== "" && !isValidUrl(trimmedValue)) {
        if (type === "bookingServiceLink") {
          setError((prev) => ({
            ...prev,
            bookingServiceError: errorMessage,
          }));
        }

        if (type === "paymentServiceLink") {
          setError((prev) => ({
            ...prev,
            paymentServiceError: errorMessage,
          }));
        }
      } else {
        setError({
          bookingServiceError: null,
          paymentServiceError: null,
        });
      }

      setUserState((prev) => ({ ...prev, [type]: trimmedValue }));
    },
    [t, isValidUrl]
  );

  const deleteHandler = useCallback(
    (onYes, promptQuestion) => {
      customPrompt({
        message: promptQuestion,
        onYes: () => {
          onYes();
        },
        onCancel: () => {
          return;
        },
      });
    },
    [actions, self, userState, t]
  );

  const deleteCoverHandler = () => {
    const deleteCover = () => {
      setUploadedCover(null);
      if (!userState?.coverUrl) {
        setUserState({
          ...userState,
          cover_id: null,
        });
      } else {
        actions.updateCoachProfile({ remove_cover: true }).then(() => {
          actions.fetchCoachProfile(self?.id).then(() => {
            setToastAlert(t("Your account was successfully updated."));
          });
        });
      }
    };

    deleteHandler(
      deleteCover,
      t("Are you sure you want to delete the cover image?")
    );
  };

  const deleteAvatarHandler = () => {
    const deleteCover = () => {
      setUploadedAvatar(null);
      if (!userState?.avatar_url) {
        setUserState({
          ...userState,
          avatar_url: null,
        });
      } else {
        actions
          .updateCoachProfile({
            remove_avatar: true,
            avatar_url: null,
          })
          .then(() => {
            actions.fetchCoachProfile(self?.id).then(() => {
              setToastAlert(t("Your account was successfully updated."));
            });
          });
      }
    };

    deleteHandler(
      deleteCover,
      t("Are you sure you want to delete the avatar?")
    );
  };

  const hasErrors = (errorState: {
    bookingServiceError: string | null;
    paymentServiceError: string | null;
  }) => {
    return Object.values(errorState).some((error) => error !== null);
  };

  const updateUser = async () => {
    if (hasErrors(error)) {
      setToastAlert(null);
      setToastErrorrAlert(
        t("Please resolve the errors before updating your account.")
      );
      return;
    }

    setLoading(true);
    try {
      if (userState) {
        await actions.updateCoachProfile({ ...userState, publicLandingPageEnabled });
        await actions.fetchCoachProfile(self?.id);

        setToastErrorrAlert(null);
        setToastAlert(t("Your account was successfully updated."));
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during update:", error);
      setToastErrorrAlert(
        t(
          "An error occurred while updating your account. Please try again later."
        )
      );
      setToastAlert(null);
      setLoading(false);
    }
  };

  const handlePublicLandingPageChange = async () => {
    const enabling = !publicLandingPageEnabled;
    const data = { public_landing_page: enabling };
    const cnData = { coach_profile: { public_landing_page_enabled: enabling }};
    const eligible = isAcademyOnboardingCoach(self);
    // Read before the save: the alert's own gate only asks whether the item
    // is complete now, so this is what keeps re-enabling the toggle from
    // re-celebrating an item the coach finished long ago.
    const completesAcademySetupItem =
      enabling && eligible && isSetupItemOpen("coach_profile");

    // One chain for every record the toggle writes — the landing page, the
    // coach profile and, when enabling, the public calendar — so the single
    // confirmation (the designed alert, or the generic toast) runs only once
    // all of them have succeeded and never sits beside a failure.
    const writes: Array<Promise<unknown>> = [
      schedulingFetchPatch(
        `coach_profiles/${self?.id}/landing_page_settings`,
        data
      ),
      fetchPut(`coach_profile`, cnData),
    ];
    if (enabling) {
      writes.push(
        schedulingFetchPatch(`coach_profiles/${self?.id}/calendar_settings`, {
          public_calendar: true,
        })
      );
    }
    return Promise.all(writes)
      .then(() => {
        setPublicLandingPageEnabled(enabling);
        if (completesAcademySetupItem) {
          // The alert refetches the checklist itself and only fires if the
          // API confirms the item — a toggle the BE doesn't count falls back
          // to the generic confirmation below.
          return showCoachProfileEnabledToast(actions.fetchAcademySetup, (path, state) =>
history.push(path, state)
          );
        }
        if (eligible) {
          // Refresh the checklist slice so the coach-profile item flips.
          actions.fetchAcademySetup();
        }
        return false;
      })
      .then((celebrated: boolean) => {
        if (!celebrated) {
          toast.success(t("Update successful."));
        }
      })
      .catch(() => {
        toast.error(t("An unexpected error has occurred."));
      });
  };

  return {
    t,
    self,
    coachData,
    userState,
    updateUserData,
    uploadedAvatar,
    onCoverChange,
    uploadedCover,
    onUpload,
    onPhoneChange,
    phoneValue,
    updateUser,
    handleUrlInputUpdate,
    setToastAlert,
    toastAlert,
    toastErrorAlert,
    deleteCoverHandler,
    deleteAvatarHandler,
    error,
    loading,
    publicLandingPageEnabled,
    publicLandingPageUrl,
    handlePublicLandingPageChange,
  };
};
